import { useEffect, useState, memo } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate';
import MediaComponent from '../../Utils/Media/media-component';
import CarouselFadeComponent from '../../Utils/Carousel/carousel-fade-component';
import { toMediaUrl } from '../../Services/ApiService';
import useRenderLog from '../../Hooks/useRenderLog';
import ContactFormLightweight from '../../Utils/Forms/ContactForm/contact-form-lightweight-component';
import { ButtonPrimary } from '../../Utils/Buttons/button-component';
import SystemDataService from '../../Services/SystemDataService';
import BlobShape from '../../Utils/Decor/blob-shape-component';
import { ASSETS } from '../../Public/assets-data';

// Landing hero mask, white with a transparent chevron window. Data-driven from
// assets-data (the S3 mediaRegistry), rendered via MediaComponent with an explicit
// width so the lambda image optimizer returns a small, resized/cached version
// instead of the full-size original (which is what made it load slowly before).
const HERO_MASK_SRC = ASSETS.BLACK_WHITE_DIAGONAL_LIGHT_BEAM_ABSTRACT_MASK;

/* ── Icons ───────────────────────────────────────────────────────────────── */
function ArrowIcon({ className = 'h-5 w-5' }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
        </svg>
    );
}

/* ── Video lightbox ──────────────────────────────────────────────────────── */
function toEmbedUrl(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
    return toMediaUrl(url); // serve our own S3 media from the site's domain
}

function VideoModal({ open, url, onClose }) {
    const embed = toEmbedUrl(url);
    if (typeof document === 'undefined') return null;
    // Portal to <body>: the hero sits inside a transformed ParallaxSection, and a
    // transformed ancestor makes `position: fixed` resolve against it (then get
    // clipped by the hero's overflow-hidden). Rendering at the body root fixes it.
    return ReactDOM.createPortal(
        <AnimatePresence>
            {open && embed && (
                <motion.div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    {embed.match(/\.(mp4|webm|ogg)$/i) ? (
                        <motion.div
                            className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black"
                            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
                            >
                                ✕
                            </button>
                            {/* Box hugs the video's own aspect ratio; grows large on desktop. */}
                            <video
                                src={embed}
                                controls
                                autoPlay
                                playsInline
                                preload="auto"
                                className="block w-auto h-auto max-w-[92vw] max-h-[86vh]"
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            className="relative w-full max-w-5xl lg:max-w-6xl aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black"
                            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
                            >
                                ✕
                            </button>
                            <iframe src={embed} title="Video" allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
                        </motion.div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

/* ── Feature-chip icons ──────────────────────────────────────────────────── */
function FeatureIcon({ name, className = 'h-5 w-5' }) {
    const common = { className, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
    if (name === 'pin') {
        return (
            <svg {...common}>
                <path d="M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11z" />
                <circle cx="12" cy="10" r="2.5" />
            </svg>
        );
    }
    if (name === 'clipboard') {
        return (
            <svg {...common}>
                <rect x="6" y="4" width="12" height="17" rx="2" />
                <path d="M9 4V3.5A1.5 1.5 0 0110.5 2h3A1.5 1.5 0 0115 3.5V4" />
                <path d="M9 12l2 2 4-4" />
            </svg>
        );
    }
    // clock (default)
    return (
        <svg {...common}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </svg>
    );
}

function PhoneIcon({ className = 'h-5 w-5' }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z" />
        </svg>
    );
}

/* ── Hero (ACTIVE: diagonal split, ClearViolations) ───────────────────────
   Dark navy on the (LTR) left carries the headline + CTAs; a light diagonal
   panel on the right carries the lead form. The standing inspector cutout
   straddles the split, sitting above the big orange check watermark and below
   the floating form card. z-order: split(0) → check+person(10/20) → form(30). */
function LandingHeroSection({ heroContent }) {
    useRenderLog('LandingHeroSection', { heroContent });
    const scrollOrNavigate = useScrollOrNavigate();
    const language = useSelector((state) => state.systemData?.language ?? 'he');
    const businessPhone = useSelector((state) => state.systemData?.businessInfo?.phone);
    const brandName = useSelector((state) => state.systemData?.businessInfo?.businessName || '');
    // Client service area/location (provisioned in businessInfo), used to fill the
    // {location} token in the hero headline, e.g. "…Installation in South Florida".
    const location = useSelector((state) => {
        const b = state.systemData?.businessInfo || {};
        return b.serviceArea || b.location || b.city || '';
    });
    const links = useSelector((state) => state.systemData?.links || []);
    // Phone icon image (optional), configured via the "Phone" link in system_data.
    const phoneLink = SystemDataService.byLabel(links, 'Phone');
    const phoneIconImage = phoneLink?.image || '';
    const [videoOpen, setVideoOpen] = useState(false);

    // Hero background carousel, pulls images.carousel from S3 (dealer photos are
    // prepended there by the hub overlay; curated brand slides follow).
    const [specialsImages, setSpecialsImages] = useState([]);
    const [specialsLoading, setSpecialsLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        const toUrl = (img) => {
            const raw = typeof img === 'string' ? img : img?.media || img?.image || img?.url || '';
            return raw ? toMediaUrl(raw) : '';
        };
        SystemDataService.getCarouselImages().then((data) => {
            if (alive && Array.isArray(data)) setSpecialsImages(data.map(toUrl).filter(Boolean));
        }).finally(() => { if (alive) setSpecialsLoading(false); });
        return () => { alive = false; };
    }, []);

    // Shown while the Specials images are still being fetched, a shimmering
    // skeleton instead of flashing the /placeholder.svg on refresh.
    const heroSkeleton = (
        <div className="relative h-full w-full overflow-hidden bg-primary-100">
            <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-primary-100 via-primary-50 to-primary-100" />
        </div>
    );

    const {
        eyebrow = '',
        heroTitleLines = [
            { text: 'PERMIT PROBLEM?', accent: false },
            { text: 'WE GET IT CLEARED.', accent: true },
        ],
        heroSubtitle = '',
        primaryCtaText = 'GET MY FREE REVIEW',
        phone = '',
        heroPerson = '',
        heroImage = '',
        features = [],
        videoUrl = '',
        primaryTarget = { id: 'contact', href: '/#contact' },
        socialProof = {},
    } = heroContent || {};

    // Social proof: overlapping reviewer avatars + a star rating + caption.
    const spAvatars = (socialProof.avatars?.length ? socialProof.avatars : [null, null, null, null]).slice(0, 5);
    const spRating = socialProof.rating || 5;
    const spText = socialProof.text || 'Trusted by homeowners across South Florida';
    const SP_FALLBACK = ['#FFC629', '#0E1A2B', '#7FB3D5', '#E8AF14'];

    const personSrc = heroPerson ? toMediaUrl(heroPerson) : '';
    const callNumber = businessPhone || phone;
    const telHref = callNumber ? `tel:${callNumber.replace(/[^\d+]/g, '')}` : null;

    // Fill {location}/{business} tokens from businessInfo. When there's no location,
    // a dangling "in" connector is dropped so the headline stays clean.
    const fillTokens = (text) => {
        if (!text) return '';
        let out = text
            .replace(/\{business(?:Name)?\}/gi, brandName)
            .replace(/\{(?:location|city|serviceArea)\}/gi, location);
        if (!location) out = out.replace(/\bin\s*$/i, '');
        return out.replace(/\s{2,}/g, ' ').trim();
    };
    // Apply tokens per line and drop any line that ends up empty (e.g. the
    // "in {location}" line when no location is provisioned).
    const titleLines = heroTitleLines
        .map((line) => ({ ...line, text: fillTokens(line.text) }))
        .filter((line) => line.text);

    const handlePrimary = (e) => scrollOrNavigate(e, primaryTarget.id, primaryTarget.href);

    return (
        <section className="relative w-full overflow-hidden bg-white text-ink">
            {/* ── Mobile hero banner ───────────────────────────────────────────
                Full-bleed from the very top (sits under the transparent navbar);
                the angled bottom edge echoes the desktop diagonal mask. Desktop
                hides this, it uses the Layer 0/1 split instead. */}
            <div
                className="relative -mt-px h-[400px] w-full overflow-hidden bg-white lg:hidden"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% 88%, 0 100%)' }}
            >
                {specialsImages.length > 0 ? (
                    <CarouselFadeComponent images={specialsImages} isBackground duration={4500} />
                ) : specialsLoading ? (
                    heroSkeleton
                ) : (
                    <MediaComponent
                        src={heroImage || '/placeholder.svg'}
                        alt=""
                        objectFit="cover"
                        useSekeleton={false}
                        wrapperClassName="h-full w-full"
                    />
                )}
            </div>
            {/* ── Layer 0: hero image on the BACK (full-bleed, desktop) ────────
                The photo fills the section; the cream mask (Layer 1) sits on top and
                carves it into the angled right-hand window. Set heroContent.heroImage
                to drop in the real photo, a styled placeholder shows until then.
                Desktop only, mobile stays flat cream so the text + form read. */}
            <div className="pointer-events-none absolute inset-0 z-0 hidden lg:block" aria-hidden="true">
                {/* Specials images cycle through the fade carousel; until any are
                    seeded in S3 we fall back to the /placeholder.svg via MediaComponent. */}
                {specialsImages.length > 0 ? (
                    <CarouselFadeComponent images={specialsImages} isBackground duration={4500} />
                ) : specialsLoading ? (
                    heroSkeleton
                ) : (
                    <MediaComponent
                        src={heroImage || '/placeholder.svg'}
                        alt=""
                        objectFit="cover"
                        useSekeleton={false}
                        quality={60}
                        wrapperClassName="h-full w-full"
                    />
                )}
            </div>

            {/* ── Layer 1: MASK image on top of the photo ───────────────────────
                A white PNG (uploaded to the gallery) whose transparent chevron
                window reveals the photo on the right; the opaque white covers the
                left so the copy + form read. Sits ABOVE the photo (z-[1] over z-0). */}
            <div className="pointer-events-none absolute inset-0 z-[1] hidden h-full w-full lg:block">
                <MediaComponent
                    src={HERO_MASK_SRC}
                    alt=""
                    width={1400}
                    quality={75}
                    eager
                    useSekeleton={false}
                    wrapperClassName="h-full w-full"
                    className="!object-fill scale-[1.03]"
                />
            </div>


            {/* ── Layer 20: OLD person/skyline cutout, disabled for the new
                masked-background hero (the photo now lives in Layer 0, behind the
                cream mask). Kept gated-off in case it's needed again. ─────────── */}
            {false && personSrc && (
                <div className="pointer-events-none absolute inset-0 z-[20] hidden lg:block">
                    <div className="relative mx-auto h-full w-full max-w-[1500px] 2xl:max-w-[1760px]">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute bottom-0 right-[10%] h-[70%] w-[65%] drop-shadow-[0_25px_45px_rgba(2,6,23,0.55)] xl:h-[75%] 2xl:h-[85%]"
                            aria-hidden="true"
                        >
                            <MediaComponent
                                src={heroPerson}
                                alt={brandName || ''}
                                objectFit="contain"
                                useSekeleton={false}
                                quality={50}
                                wrapperClassName="h-full w-full"
                                className="object-bottom"
                            />
                        </motion.div>
                    </div>
                </div>
            )}

            {/* ── Layer 30: content grid (headline + form) ──────────────────── */}
            <div className="relative z-30 mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-8 px-6 pt-8 pb-12 sm:px-10 sm:pt-10 lg:grid-cols-[minmax(0,1fr)_460px] lg:gap-10 lg:px-16 lg:min-h-screen lg:pt-52 lg:pb-40 xl:pt-56 2xl:max-w-[1760px]">

                {/* Text column (left / dark) */}
                <div className="flex w-full min-w-0 flex-col items-center text-center lg:max-w-[600px] lg:items-start lg:text-left xl:max-w-[680px] 2xl:max-w-[760px]">

                    {/* Eyebrow, commented out per request
                    {eyebrow && (
                        <motion.p
                            className="mb-4 text-sm font-bold uppercase tracking-[0.15em] text-secondary-400"
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                        >
                            {eyebrow}
                        </motion.p>
                    )}
                    */}

                    <h1 className="w-full max-w-[calc(100vw-3rem)] font-secondary text-[30px] font-extrabold uppercase leading-[1.12] tracking-tight [hyphens:none] sm:text-5xl sm:leading-[1.08] lg:max-w-none lg:text-[56px] xl:text-[64px] 2xl:text-[68px]">
                        {titleLines.map((line, i) => (
                            <motion.span
                                key={i}
                                className={`block ${line.accent ? 'text-secondary-500' : 'text-navy'}`}
                                initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: i * 0.12 }}
                            >
                                {line.text}
                            </motion.span>
                        ))}
                    </h1>

                    {heroSubtitle && (
                        <motion.p
                            className="mt-6 max-w-[calc(100vw-3rem)] text-base leading-relaxed text-ink-muted sm:max-w-md"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            {heroSubtitle}
                        </motion.p>
                    )}

                    {/* Mobile-only hero visual, disabled with the new masked-bg hero. */}
                    {false && personSrc && (
                        <div className="relative mt-8 flex h-[420px] w-screen -mx-6 items-end justify-center overflow-hidden sm:-mx-10 lg:hidden" aria-hidden="true">
                            {/* Filled white diagonal panel (echoes the desktop split) */}
                            <div className="absolute inset-0 bg-white" style={{ clipPath: 'polygon(0 40%, 100% 18%, 100% 100%, 0 100%)' }} />
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(169deg, transparent calc(29% - 2px), rgba(255,255,255,0.9) 29%, transparent calc(29% + 2px))' }} />
                            <motion.div
                                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                                className="relative z-10 h-[420px] w-full drop-shadow-[0_20px_35px_rgba(2,6,23,0.4)]"
                            >
                                <MediaComponent
                                    src={heroPerson}
                                    alt=""
                                    objectFit="contain"
                                    useSekeleton={false}
                                    quality={50}
                                    wrapperClassName="h-full w-full"
                                    className="object-bottom"
                                />
                            </motion.div>
                        </div>
                    )}

                    {/* Trust chips */}
                    {features.length > 0 && (
                        <div className="mt-8 flex flex-wrap items-start justify-center gap-x-6 gap-y-4 lg:justify-start">
                            {features.map((f, i) => (
                                <motion.div
                                    key={f.title}
                                    className="flex items-center gap-2.5 text-left"
                                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.45, delay: 0.5 + i * 0.1 }}
                                >
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-secondary-500 text-secondary-500">
                                        <FeatureIcon name={f.icon} className="h-5 w-5" />
                                    </span>
                                    <span className="leading-tight">
                                        <span className="block text-sm font-bold uppercase tracking-wide text-navy">{f.title}</span>
                                        <span className="block whitespace-pre-line text-xs text-ink-muted">{f.sub}</span>
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* CTA + call (stacked: primary on top, transparent call below) */}
                    <div className="mt-10 flex w-full max-w-[360px] flex-col items-stretch gap-4">
                        <ButtonPrimary
                            onClick={handlePrimary}
                            dir="ltr"
                            className="!w-full !px-8 !py-4 !text-base normal-case"
                            icon={<ArrowIcon className="h-5 w-5 shrink-0" />}
                        >
                            {primaryCtaText}
                        </ButtonPrimary>

                        {callNumber && (
                            <a
                                href={telHref}
                                dir="ltr"
                                className="group flex items-center justify-center gap-2.5 rounded-full border border-ink/20 px-8 py-4 text-ink transition-all duration-300 hover:border-ink/40 hover:bg-black/5 active:scale-[0.98]"
                            >
                                {phoneIconImage
                                    ? <MediaComponent src={phoneIconImage} useSekeleton={false} alt="Call" wrapperClassName="h-5 w-5 shrink-0" objectFit="contain" />
                                    : <PhoneIcon className="h-5 w-5 shrink-0 text-secondary-400" />
                                }
                                <span className="text-xs font-semibold tracking-wide text-ink-muted">Call now</span>
                                <span className="font-secondary text-base font-bold text-navy">{callNumber}</span>
                            </a>
                        )}
                    </div>

                    {/* Social proof, reviewer avatars + star rating */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }}
                        className="mt-8 flex items-center justify-center gap-3.5 lg:justify-start"
                    >
                        <div className="flex -space-x-3">
                            {spAvatars.map((a, i) => (
                                a ? (
                                    <span key={i} className="h-10 w-10 overflow-hidden rounded-full ring-2 ring-white shadow-sm">
                                        <MediaComponent src={a} alt="" objectFit="cover" useSekeleton={false} wrapperClassName="h-full w-full" className="h-full w-full object-cover" />
                                    </span>
                                ) : (
                                    <span key={i} className="h-10 w-10 rounded-full ring-2 ring-white shadow-sm" style={{ backgroundColor: SP_FALLBACK[i % SP_FALLBACK.length] }} />
                                )
                            ))}
                        </div>
                        <div className="text-left">
                            <div className="flex gap-0.5">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <svg key={i} viewBox="0 0 20 20" className={`h-4 w-4 ${i < spRating ? 'text-secondary-500' : 'text-primary-100'}`} fill="currentColor" aria-hidden="true">
                                        <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.98l-5.2 2.74.99-5.8L1.58 7.62l5.82-.85L10 1.5z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="mt-0.5 text-sm text-ink-muted">{spText}</p>
                        </div>
                    </motion.div>
                </div>

                {/* Form card (right / light), fixed-width column so it never squishes */}
                <motion.div
                    className="relative w-full max-w-md justify-self-center lg:max-w-none lg:justify-self-end"
                    initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
                >
                    {/* Decorative gold blob behind the form, one big centered shape,
                        recolored via the secondary token. */}
                    <BlobShape
                        src={toMediaUrl(ASSETS.GOLDEN_YELLOW_BLOB_SHAPE_GRAPHIC)}
                        colorClass="bg-secondary-500"
                        className="flex lg:hidden left-1/2 top-1/2 -z-10 h-[230%] w-[200%] -translate-x-1/2 -translate-y-1/2 "
                    />
                    <div className="relative rounded-[22px] border border-white/60 bg-white p-6 shadow-2xl shadow-black/30 sm:p-7">
                        <h3 className="font-secondary text-2xl font-extrabold text-primary-500">
                            GET YOUR FREE QUOTE
                        </h3>
                        <p className="mt-2 mb-5 text-sm leading-relaxed text-primary-300">
                            Send us a few details and we’ll get back to you with a free quote.
                        </p>
                        <ContactFormLightweight />
                        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-primary-300">
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" />
                            </svg>
                            Your information is 100% secure
                        </p>
                    </div>
                </motion.div>
            </div>

            {/* ── Mobile-only image carousel below the form ─────────────────────
                Mirrors the top banner: the top banner's bottom edge slopes down to
                the left, so this one's top edge slopes down to the right, the two
                diagonals frame the form symmetrically. Desktop hides this. */}
            <div
                className="relative -mt-2 h-[300px] w-full overflow-hidden bg-white lg:hidden"
                style={{ clipPath: 'polygon(0 0, 100% 12%, 100% 100%, 0 100%)' }}
            >
                {specialsImages.length > 0 ? (
                    <CarouselFadeComponent images={specialsImages} isBackground duration={4500} />
                ) : specialsLoading ? (
                    heroSkeleton
                ) : (
                    <MediaComponent
                        src={heroImage || '/placeholder.svg'}
                        alt=""
                        objectFit="cover"
                        useSekeleton={false}
                        eager
                        wrapperClassName="h-full w-full"
                    />
                )}
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: 'linear-gradient(to top, rgba(14,26,43,0.35) 0%, transparent 45%)' }}
                />
            </div>

            <VideoModal open={videoOpen} url={videoUrl} onClose={() => setVideoOpen(false)} />
        </section>
    );
}

/* ── Hero (UNUSED: light two-column design, kept for reference, no eyebrow) ──
   NOTE: eyebrow removed per request. Not currently rendered. */
function LandingHeroSectionLight({ heroContent }) {
    const scrollOrNavigate = useScrollOrNavigate();
    const {
        eyebrow,
        heroTitleLines = [],
        heroSubtitle = '',
        chips = [],
        stats = [],
        primaryCtaText = 'Start Free Review',
        watchCtaText = 'Our Services',
        primaryTarget = { id: 'contact', href: '/#contact' },
        watchTarget = { id: 'services', href: '/#services' },
        image = '',
        imageAlt = '',
    } = heroContent || {};

    const handlePrimary = (e) => scrollOrNavigate(e, primaryTarget.id, primaryTarget.href);
    const handleWatch = (e) => scrollOrNavigate(e, watchTarget.id, watchTarget.href);

    return (
        <section className="relative w-full overflow-hidden bg-white pt-10 pb-16 lg:pb-24">
            <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
                {/* Left: copy */}
                <div>
                    <h1 className="font-secondary text-[40px] font-bold leading-[1.08] tracking-[-0.03em] text-primary-500 sm:text-[56px]">
                        {heroTitleLines.map((line, i) => (
                            <span key={i} className={line.accent ? 'text-secondary-500' : ''}>
                                {i > 0 ? ' ' : ''}{line.text}
                            </span>
                        ))}
                    </h1>
                    {heroSubtitle && (
                        <p className="mt-5 max-w-xl text-lg leading-relaxed text-primary-300">{heroSubtitle}</p>
                    )}

                    {chips.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                            {chips.map((c) => (
                                <span key={c} className="rounded-full border border-primary-100 bg-primary-50 px-3.5 py-1.5 text-sm text-primary-500">{c}</span>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <button onClick={handlePrimary} className="inline-flex items-center gap-2 rounded-full bg-secondary-500 px-6 py-3 font-secondary font-semibold text-white transition hover:bg-primary-500">
                            {primaryCtaText} <span aria-hidden>→</span>
                        </button>
                        <button onClick={handleWatch} className="inline-flex items-center gap-2 rounded-full border border-primary-200 px-6 py-3 font-secondary font-semibold text-primary-500 transition hover:bg-primary-500 hover:text-white">
                            {watchCtaText}
                        </button>
                    </div>
                </div>

                {/* Right: photo + orange stats block */}
                <div className="relative">
                    {image ? (
                        <MediaComponent src={image} alt={imageAlt} width={800} quality={75} useSekeleton={false}
                            wrapperClassName="aspect-[4/3] w-full overflow-hidden rounded-[14px] lg:aspect-[16/10]" />
                    ) : (
                        <div className="flex aspect-[4/3] w-full items-center justify-center rounded-[14px] border border-primary-100 text-center text-xs font-medium uppercase tracking-wide text-primary-300 lg:aspect-[16/10]"
                            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#EAEEF7 0 10px,#CBD7EC 10px 20px)' }}>
                            <span className="rounded-full bg-white/80 px-3 py-1">{imageAlt || 'Hero photo, inspector / job site'}</span>
                        </div>
                    )}
                    {stats.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-[14px] bg-secondary-500 text-white lg:absolute lg:-bottom-6 lg:right-6 lg:mt-0 lg:w-[72%]">
                            {stats.map((s) => (
                                <div key={s.label} className="bg-secondary-500 px-4 py-4">
                                    <div className="font-secondary text-lg font-bold leading-none">{s.label}</div>
                                    <div className="mt-1 text-[13px] leading-snug text-white/85">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

// Memoized: its props (heroContent) are stable, so it skips the re-render
// cascade when the Landing parent re-renders for unrelated systemData updates.
export default memo(LandingHeroSection);
